import { useState, useMemo, useRef } from "react";
import { useParams } from "react-router";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/ui/page-header";
import { IconChevronDown } from "@tabler/icons-react";
import { useFleetDbHeightMeasurements } from "@/api/hooks/use-fleet-measurements";
import { useEvent } from "@/api/hooks";
import { TeamFlag } from "@/components/team-flag";
import { timeAgo } from "@/lib/time";
import { asEventId, type EventId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";
import {
  CheckedCard,
  ComparisonTeamPills,
  FLEET_ROW_INNER,
  PORT_COLOR,
  STBD_COLOR,
  teamColor,
  useToggleSet,
  type ChartTooltip,
} from "./_shared";

type FleetDbHeightTeamEntry = components["schemas"]["FleetDbHeightTeamEntry-Output"];
type FleetDbHeightHistoryEntry = components["schemas"]["FleetDbHeightHistoryEntry"];
type DbVoltageRange = components["schemas"]["DbVoltageRange"];

const VOLT_DECIMALS = 3;
const RANGE_DECIMALS = 3;

function fmtV(v: number | null | undefined): string {
  return v == null ? "—" : v.toFixed(VOLT_DECIMALS);
}

function rangeOf(r: DbVoltageRange | null | undefined): number | null {
  return r ? r.max - r.min : null;
}

function dataRange(values: number[]): { yMin: number; yMax: number } {
  if (!values.length) return { yMin: 0, yMax: 5 };
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const span = Math.max(hi - lo, 0.5);
  const pad = span * 0.1;
  return { yMin: Math.max(0, lo - pad), yMax: hi + pad };
}

/** Inline sparkline showing the per-side voltage range (max-min) over events. */
function RangeSparkline({ history }: { history: FleetDbHeightHistoryEntry[] }) {
  const w = 140, h = 22, px = 6, py = 3;

  const portRanges = history.map((h) => rangeOf(h.port)).filter((v): v is number => v != null);
  const stbdRanges = history.map((h) => rangeOf(h.starboard)).filter((v): v is number => v != null);
  const allVals = [...portRanges, ...stbdRanges];
  if (!allVals.length) return null;
  const { yMin, yMax } = dataRange(allVals);

  const xf = (i: number) => history.length === 1 ? w / 2 : px + (i / (history.length - 1)) * (w - px * 2);
  const yf = (v: number) => h - py - ((v - yMin) / (yMax - yMin)) * (h - py * 2);

  function line(side: "port" | "starboard", color: string) {
    const points = history
      .map((h, i) => ({ i, v: rangeOf(side === "port" ? h.port : h.starboard) }))
      .filter((p): p is { i: number; v: number } => p.v != null);
    if (!points.length) return null;
    if (points.length === 1) {
      return <circle cx={xf(points[0].i)} cy={yf(points[0].v)} r={2.5} fill={color} stroke="var(--card)" strokeWidth={1} />;
    }
    return (
      <>
        <path d={points.map((p, j) => `${j === 0 ? "M" : "L"}${xf(p.i)},${yf(p.v)}`).join(" ")} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
        {points.map((p, j) => (
          <circle key={j} cx={xf(p.i)} cy={yf(p.v)} r={j === points.length - 1 ? 2.5 : 1.5} fill={color} stroke={j === points.length - 1 ? "var(--card)" : "none"} strokeWidth={1} opacity={j === points.length - 1 ? 1 : 0.6} />
        ))}
      </>
    );
  }

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="block w-full">
      {line("port", PORT_COLOR)}
      {line("starboard", STBD_COLOR)}
    </svg>
  );
}

function HistoryTable({ entry }: { entry: FleetDbHeightTeamEntry }) {
  if (!entry.history.length) return null;
  const reversed = [...entry.history].reverse();

  return (
    <div className="overflow-x-auto -mx-4">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-border">
            <th className="py-1 px-2 text-[10px] font-bold text-muted-foreground uppercase tracking-wider" rowSpan={2}>Event</th>
            <th className="py-1 px-2 text-[10px] font-bold uppercase tracking-wider text-center border-b border-border/30" colSpan={3} style={{ color: PORT_COLOR }}>Port (V)</th>
            <th className="py-1 px-2 text-[10px] font-bold uppercase tracking-wider text-center border-b border-border/30" colSpan={3} style={{ color: STBD_COLOR }}>Starboard (V)</th>
            <th className="py-1 px-2 text-[10px] font-bold text-muted-foreground uppercase tracking-wider text-right" rowSpan={2}>Fetched</th>
          </tr>
          <tr className="border-b border-border">
            {["min", "max", "Δ", "min", "max", "Δ"].map((h, i) => (
              <th key={i} className="py-1 px-2 text-[9px] font-semibold text-muted-foreground uppercase text-center">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {reversed.map((h, ri) => {
            const isLatest = ri === 0;
            const portRange = rangeOf(h.port);
            const stbdRange = rangeOf(h.starboard);
            return (
              <tr key={`${h.event_name}-${h.fetched_at}`} className={`${isLatest ? "bg-blue-50/30 dark:bg-blue-950/20" : "hover:bg-muted/30"} border-b border-border/30`}>
                <td className="py-1.5 px-2 text-xs text-muted-foreground font-medium whitespace-nowrap">
                  {h.event_name}
                  {isLatest && <span className="ml-1 text-[9px] text-blue-500 font-semibold uppercase">Latest</span>}
                </td>
                <td className="py-1.5 px-2 text-xs tabular-nums text-center">{fmtV(h.port?.min)}</td>
                <td className="py-1.5 px-2 text-xs tabular-nums text-center">{fmtV(h.port?.max)}</td>
                <td className="py-1.5 px-2 text-[11px] tabular-nums text-center text-muted-foreground">{portRange != null ? portRange.toFixed(RANGE_DECIMALS) : "—"}</td>
                <td className="py-1.5 px-2 text-xs tabular-nums text-center">{fmtV(h.starboard?.min)}</td>
                <td className="py-1.5 px-2 text-xs tabular-nums text-center">{fmtV(h.starboard?.max)}</td>
                <td className="py-1.5 px-2 text-[11px] tabular-nums text-center text-muted-foreground">{stbdRange != null ? stbdRange.toFixed(RANGE_DECIMALS) : "—"}</td>
                <td className="py-1.5 px-2 text-xs text-muted-foreground text-right whitespace-nowrap">{timeAgo(h.fetched_at)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Per-side comparison chart: each team plotted as a vertical band [min, max] per event. */
function ComparisonSideChart({ teams, allEvents, side, color, label }: {
  teams: { code: string; history: FleetDbHeightHistoryEntry[] }[];
  allEvents: string[];
  side: "port" | "starboard";
  color: string;
  label: string;
}) {
  const [tooltip, setTooltip] = useState<ChartTooltip | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const showTooltip = (e: React.MouseEvent, text: string) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top - 8, text });
  };

  // Fixed y-axis covering the sensor's full theoretical range so all teams
  // are read on the same scale. Gridlines pinned at 1/2/3/4 V.
  const yMin = 0;
  const yMax = 5;
  const ticks = [1, 2, 3, 4];

  const w = 400, h = 170, px = 44, py = 25;
  const xf = (i: number) => allEvents.length === 1 ? w / 2 : px + (i / (allEvents.length - 1)) * (w - px * 2);
  const yf = (v: number) => h - py - ((v - yMin) / (yMax - yMin)) * (h - py * 2);

  // Horizontal jitter per team so multiple teams' vertical bands don't sit on
  // top of each other at the same event. Capped so neighbouring events stay
  // distinguishable.
  const eventGap = allEvents.length > 1 ? (w - 2 * px) / (allEvents.length - 1) : w - 2 * px;
  const spacing = teams.length > 1 ? Math.min(7, (eventGap * 0.45) / Math.max(1, teams.length - 1)) : 0;

  return (
    <div className="relative" onMouseLeave={() => setTooltip(null)}>
      {tooltip && (
        <div className="absolute z-10 px-2 py-1 rounded bg-gray-900 text-white text-[10px] tabular-nums whitespace-nowrap pointer-events-none" style={{ left: tooltip.x, top: tooltip.y, transform: "translate(-50%, -100%)" }}>
          {tooltip.text}
        </div>
      )}
      <svg ref={svgRef} viewBox={`0 0 ${w} ${h}`} className="block w-full" preserveAspectRatio="xMidYMid meet">
        {ticks.map((v) => (
          <g key={v}>
            <text x={px - 6} y={yf(v) + 3} textAnchor="end" fill="var(--muted-foreground)" fontSize={9} style={{ fontFamily: "ui-sans-serif, system-ui, sans-serif" }}>{v.toFixed(2)}</text>
            <line x1={px} y1={yf(v)} x2={w - px} y2={yf(v)} stroke="var(--border)" strokeWidth={1} />
          </g>
        ))}
        {allEvents.map((ev, i) => (
          <text key={ev} x={xf(i)} y={h - 5} textAnchor="middle" fill="var(--muted-foreground)" fontSize={9} style={{ fontFamily: "ui-sans-serif, system-ui, sans-serif" }}>{ev}</text>
        ))}
        <text x={px - 6} y={py - 8} textAnchor="end" fill="var(--muted-foreground)" fontSize={8} fontStyle="italic">V</text>
        <text x={w / 2} y={py - 8} textAnchor="middle" fill={color} fontSize={10} fontWeight={600}>{label}</text>
        {teams.map(({ code, history }, teamIndex) => {
          const teamHex = teamColor(code);
          const points = history
            .map((h) => {
              const idx = allEvents.indexOf(h.event_name);
              const r = side === "port" ? h.port : h.starboard;
              return idx === -1 || !r ? null : { i: idx, min: r.min, max: r.max, ev: h.event_name };
            })
            .filter(Boolean) as { i: number; min: number; max: number; ev: string }[];
          if (!points.length) return null;
          points.sort((a, b) => a.i - b.i);
          const xOffset = (teamIndex - (teams.length - 1) / 2) * spacing;
          const xt = (i: number) => xf(i) + xOffset;
          const last = points[points.length - 1];
          const minPath = points.map((p, j) => `${j === 0 ? "M" : "L"}${xt(p.i)},${yf(p.min)}`).join(" ");
          const maxPath = points.map((p, j) => `${j === 0 ? "M" : "L"}${xt(p.i)},${yf(p.max)}`).join(" ");
          return (
            <g key={code}>
              {points.length > 1 && (
                <>
                  <path d={minPath} fill="none" stroke={teamHex} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" opacity={0.55} />
                  <path d={maxPath} fill="none" stroke={teamHex} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" opacity={0.55} />
                </>
              )}
              {points.map((p, j) => {
                const isLast = j === points.length - 1;
                return (
                  <g key={j}>
                    <circle cx={xt(p.i)} cy={yf(p.min)} r={isLast ? 3 : 2} fill={teamHex} stroke="var(--card)" strokeWidth={1} style={{ cursor: "pointer" }}
                      onMouseEnter={(e) => showTooltip(e, `${code} ${p.ev}: min ${p.min.toFixed(VOLT_DECIMALS)} V`)} />
                    <circle cx={xt(p.i)} cy={yf(p.max)} r={isLast ? 3 : 2} fill={teamHex} stroke="var(--card)" strokeWidth={1} style={{ cursor: "pointer" }}
                      onMouseEnter={(e) => showTooltip(e, `${code} ${p.ev}: max ${p.max.toFixed(VOLT_DECIMALS)} V`)} />
                  </g>
                );
              })}
              <text
                x={xt(last.i)}
                y={yf(last.max) - 6}
                fill={teamHex}
                fontSize={9}
                fontWeight={600}
                textAnchor="middle"
                style={{
                  fontFamily: "ui-sans-serif, system-ui, sans-serif",
                  paintOrder: "stroke fill",
                  stroke: "var(--card)",
                  strokeWidth: 2,
                }}
              >
                {code}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function SideRangeBar({ range, color, label }: { range: DbVoltageRange | null | undefined; color: string; label: string }) {
  if (!range) {
    return (
      <div className="flex items-center gap-2 text-xs">
        <span className="text-[9px] font-bold w-3 shrink-0" style={{ color }}>{label}</span>
        <span className="text-muted-foreground italic">—</span>
      </div>
    );
  }
  const span = range.max - range.min;
  return (
    <div className="flex items-baseline gap-2 text-xs tabular-nums">
      <span className="text-[9px] font-bold w-3 shrink-0" style={{ color }}>{label}</span>
      <span className="font-semibold">{range.min.toFixed(VOLT_DECIMALS)}</span>
      <span className="text-muted-foreground/60">–</span>
      <span className="font-semibold">{range.max.toFixed(VOLT_DECIMALS)}</span>
      <span className="text-[10px] text-muted-foreground">V</span>
      <span className="text-[10px] text-muted-foreground/70 ml-auto">Δ {span.toFixed(RANGE_DECIMALS)}</span>
    </div>
  );
}

export function DbHeightPage() {
  const { eventId: rawEventId } = useParams<{ eventId: string }>();
  const eventId = asEventId(rawEventId ?? "") as EventId;
  const { data: event } = useEvent(eventId);
  const { data: teams, isLoading } = useFleetDbHeightMeasurements(eventId);
  const [expandedTeam, setExpandedTeam] = useState<string | null>(null);
  const [comparedTeams, toggleCompare] = useToggleSet<string>();

  const allEvents = useMemo(() => {
    if (!teams) return [];
    const evMap = new Map<string, string>();
    teams.forEach((t) => t.history.forEach((h) => { if (!evMap.has(h.event_name)) evMap.set(h.event_name, h.event_date); }));
    return [...evMap.entries()].sort((a, b) => a[1].localeCompare(b[1])).map(([name]) => name);
  }, [teams]);

  const comparedData = useMemo(() => {
    if (!teams) return [];
    return [...comparedTeams].map((code) => {
      const t = teams.find((e) => e.team_code === code);
      return { code, history: t?.history ?? [] };
    }).filter((d) => d.history.length > 0);
  }, [comparedTeams, teams]);

  if (isLoading) {
    return (
      <div>
        <PageHeader title="DB Height" subtitle={event?.name} />
        <div className="mt-6 space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}</div>
      </div>
    );
  }
  if (!teams) return null;

  const checked = teams.filter((t) => t.latest_port != null || t.latest_starboard != null).length;
  const portRanges = teams.map((t) => rangeOf(t.latest_port)).filter((v): v is number => v != null);
  const stbdRanges = teams.map((t) => rangeOf(t.latest_starboard)).filter((v): v is number => v != null);
  const avgPortRange = portRanges.length ? portRanges.reduce((a, b) => a + b, 0) / portRanges.length : null;
  const avgStbdRange = stbdRanges.length ? stbdRanges.reduce((a, b) => a + b, 0) / stbdRanges.length : null;

  return (
    <div>
      <PageHeader title="DB Height" subtitle={event?.name} count={teams.length} countLabel="teams" />
      <p className="text-[11px] text-muted-foreground mt-1">
        Daggerboard position-sensor calibration: voltage when the board is fully retracted (min) vs fully extended (max).
      </p>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
        <CheckedCard checked={checked} total={teams.length} />
        <div className="bg-card border-l-3 rounded-r-lg pl-4 pr-5 py-3 flex items-center gap-3" style={{ borderLeftColor: PORT_COLOR }}>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap" style={{ color: PORT_COLOR }}>Avg Range · Port</div>
            <div className="text-lg font-bold tabular-nums">{avgPortRange != null ? `${avgPortRange.toFixed(RANGE_DECIMALS)} V` : "—"}</div>
          </div>
        </div>
        <div className="bg-card border-l-3 rounded-r-lg pl-4 pr-5 py-3 flex items-center gap-3" style={{ borderLeftColor: STBD_COLOR }}>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap" style={{ color: STBD_COLOR }}>Avg Range · Starboard</div>
            <div className="text-lg font-bold tabular-nums">{avgStbdRange != null ? `${avgStbdRange.toFixed(RANGE_DECIMALS)} V` : "—"}</div>
          </div>
        </div>
      </div>

      {comparedTeams.size > 0 && comparedData.length > 0 && (
        <div className="mt-6 bg-card rounded-lg border overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold">Fleet Comparison</span>
              <span className="text-[10px] text-muted-foreground">Min–max calibration voltages per event</span>
            </div>
            <ComparisonTeamPills
              codes={comparedTeams}
              teamLookup={(code) => teams.find((e) => e.team_code === code)}
              onRemove={toggleCompare}
            />
          </div>
          <div className="grid grid-cols-2">
            <div className="p-4 border-r">
              <ComparisonSideChart teams={comparedData} allEvents={allEvents} side="port" color={PORT_COLOR} label="Port" />
            </div>
            <div className="p-4">
              <ComparisonSideChart teams={comparedData} allEvents={allEvents} side="starboard" color={STBD_COLOR} label="Starboard" />
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-col gap-2">
        {teams.map((t) => {
          const fromCurrent = t.latest_port != null || t.latest_starboard != null;
          // Fall back to the most recent historical golden when the current
          // event has no usable cal (boats often don't reset their goldens
          // each event — last known calibration is still informative).
          const fallback = !fromCurrent && t.history.length > 0 ? t.history[t.history.length - 1] : null;
          const displayPort = t.latest_port ?? fallback?.port ?? null;
          const displayStarboard = t.latest_starboard ?? fallback?.starboard ?? null;
          const displayFetchedAt = t.latest_fetched_at ?? fallback?.fetched_at ?? null;
          const sourceEvent = fallback?.event_name ?? null;
          const hasData = displayPort != null || displayStarboard != null;
          const isExpanded = expandedTeam === t.team_code;
          const isCompared = comparedTeams.has(t.team_code);
          const borderClass = !hasData
            ? "border-l-border"
            : fromCurrent
              ? "border-l-blue-400"
              : "border-l-muted-foreground/30";

          const ageEl = (
            <span className="text-xs text-muted-foreground whitespace-nowrap">{hasData && displayFetchedAt ? timeAgo(displayFetchedAt) : "—"}</span>
          );
          const chevron = hasData ? (
            <IconChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${isExpanded ? "rotate-180" : ""}`} />
          ) : null;

          return (
            <div key={t.team_code} className={`border-l-3 rounded-r-lg bg-card ${borderClass}`}>
              <div
                className={`${FLEET_ROW_INNER} ${isExpanded ? "shadow-[0_1px_4px_rgba(0,0,0,0.04)]" : ""}`}
                onClick={() => hasData && setExpandedTeam(isExpanded ? null : t.team_code)}
              >
                <div className="flex items-center gap-3 sm:w-28 sm:shrink-0">
                  <div className="w-4 flex items-center justify-center shrink-0">
                    {hasData && (
                      <input type="checkbox" className="w-3.5 h-3.5 rounded border-border accent-blue-500 cursor-pointer"
                        checked={isCompared}
                        onChange={() => toggleCompare(t.team_code)}
                        onClick={(e) => e.stopPropagation()} />
                    )}
                  </div>
                  <TeamFlag iso_alpha2={t.team_iso_alpha2} size="xs" />
                  <span className="font-medium text-[13px]">{t.team_code}</span>
                  {/* Mobile only: age on the identity line */}
                  <div className="flex items-center gap-2 ml-auto sm:hidden">
                    {ageEl}
                    {chevron}
                  </div>
                </div>

                <div className="min-w-0 sm:flex-1 sm:px-3">
                  {hasData ? (
                    <div className="flex flex-col gap-0.5">
                      <SideRangeBar range={displayPort} color={PORT_COLOR} label="P" />
                      <SideRangeBar range={displayStarboard} color={STBD_COLOR} label="S" />
                      {sourceEvent && (
                        <span className="text-[10px] text-muted-foreground/70 italic">from {sourceEvent}</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground italic">No DB calibration data</span>
                  )}
                </div>

                <div className="w-[180px] shrink-0 px-2 hidden md:block">
                  {hasData && (t.history.length > 0
                    ? <RangeSparkline history={t.history} />
                    : <span className="text-[10px] text-muted-foreground italic">No history</span>)}
                </div>

                <div className="hidden sm:flex items-center gap-2 w-24 shrink-0 justify-end">
                  {ageEl}
                  {chevron}
                </div>
              </div>

              {isExpanded && hasData && (
                <div className="border-t border-border/50 px-4 py-3">
                  <HistoryTable entry={t} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
